<template>
  <Head title="Kelola Widgets" />
  <Container>
    <template #header>
      <Header :menu="$page.props.main_menu" v-if="$page.props.auth.user" />
    </template>
    <template #default>
      <div>
        <Breadcrumbs
          :breadcrumbs="[
            { name: 'Dashboard', href: route('dashboard') },
            { name: 'Kelola Widgets', href: route('widgets.index') },
          ]"
        />

        <div class="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
          <div class="order-2 col-span-1 space-y-6 lg:order-1">
            <div class="bg-white border border-gray-200 shadow-lg rounded-xl">
              <div
                class="px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-t-xl sm:px-6 sm:py-4"
              >
                <h2
                  class="flex items-center text-lg font-bold text-white sm:text-xl"
                >
                  <i class="mr-2 fa-solid fa-puzzle-piece sm:mr-3"></i>
                  {{ isEditMode ? "Edit Widget" : "Tambah Widget" }}
                </h2>
              </div>
              <div class="p-4 sm:p-6">
                <form class="space-y-4" @submit.prevent="submitForm">
                  <!-- Widget Info -->
                  <div>
                    <label
                      for="name"
                      class="block mb-2 text-sm font-medium text-gray-900"
                    >
                      Nama Widget
                    </label>
                    <input
                      v-model="form.name"
                      type="text"
                      id="name"
                      class="block w-full px-3 py-2.5 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors duration-200"
                      placeholder="Masukkan nama widget"
                      required
                    />
                    <div
                      v-if="form.errors.name"
                      class="mt-1 text-sm text-red-600"
                    >
                      {{ form.errors.name }}
                    </div>
                  </div>

                  <div>
                    <!-- {{ availableComponents }} -->
                    <label
                      for="component"
                      class="block mb-2 text-sm font-medium text-gray-900"
                    >
                      Komponen
                    </label>
                    <select
                      v-model="form.component"
                      id="component"
                      class="block w-full px-3 py-2.5 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors duration-200"
                      required
                    >
                      <option value="">Pilih Komponen</option>
                      <option
                        v-for="(
                          displayName, componentPath
                        ) in props.availableComponents"
                        :key="displayName"
                        :value="displayName"
                      >
                        {{ componentPath }}
                      </option>
                    </select>
                    <div
                      v-if="form.errors.component"
                      class="mt-1 text-sm text-red-600"
                    >
                      {{ form.errors.component }}
                    </div>
                  </div>

                  <div>
                    <label
                      for="description"
                      class="block mb-2 text-sm font-medium text-gray-900"
                    >
                      Deskripsi
                    </label>
                    <textarea
                      v-model="form.description"
                      id="description"
                      rows="3"
                      class="block w-full px-3 py-2.5 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors duration-200"
                      placeholder="Masukkan deskripsi widget"
                    ></textarea>
                    <div
                      v-if="form.errors.description"
                      class="mt-1 text-sm text-red-600"
                    >
                      {{ form.errors.description }}
                    </div>
                  </div>

                  <div>
                    <label
                      for="order"
                      class="block mb-2 text-sm font-medium text-gray-900"
                    >
                      Urutan
                    </label>
                    <input
                      v-model="form.order"
                      type="number"
                      id="order"
                      min="0"
                      class="block w-full px-3 py-2.5 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors duration-200"
                      placeholder="0"
                    />
                    <div
                      v-if="form.errors.order"
                      class="mt-1 text-sm text-red-600"
                    >
                      {{ form.errors.order }}
                    </div>
                  </div>

                  <div>
                    <label class="flex items-center">
                      <input
                        type="checkbox"
                        v-model="form.is_active"
                        class="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                      />
                      <span class="ml-2 text-sm text-gray-700">Aktif</span>
                    </label>
                  </div>

                  <div class="flex space-x-3">
                    <button
                      type="submit"
                      class="px-4 py-2 text-sm font-medium text-white transition-colors duration-200 bg-indigo-600 rounded-lg shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                      :disabled="form.processing"
                    >
                      {{ isEditMode ? "Update" : "Simpan" }}
                    </button>
                    <button
                      v-if="isEditMode"
                      type="button"
                      class="px-4 py-2 text-sm font-medium text-gray-700 transition-colors duration-200 bg-gray-200 rounded-lg shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
                      @click="cancelEdit"
                    >
                      Batal
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <div class="order-1 col-span-1 space-y-6 lg:order-2 lg:col-span-2">
            <!-- Header Panel -->
            <div
              class="p-4 bg-white border border-gray-200 shadow-sm rounded-xl sm:p-6"
            >
              <div class="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h1 class="text-xl font-bold text-gray-900">Daftar Widget</h1>
                  <p class="mt-1 text-sm text-gray-500">
                    Kelola widget yang akan ditampilkan di halaman beranda
                  </p>
                </div>
                <div class="mt-2 text-sm text-gray-500 sm:mt-0">
                  Total: {{ widgets.length }} widget
                </div>
              </div>
            </div>

            <!-- Search Input -->
            <div class="relative mb-4">
              <div
                class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none"
              >
                <i class="w-4 h-4 text-gray-400 fas fa-search"></i>
              </div>
              <input
                v-model="search"
                type="text"
                autocomplete="off"
                placeholder="Cari widget..."
                id="search"
                class="block w-full px-3 py-2.5 pl-10 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors duration-200"
              />
            </div>
            <!-- Table -->
            <div
              class="overflow-hidden bg-white shadow ring-1 ring-black ring-opacity-5 md:rounded-lg"
            >
              <div class="p-3 border-b border-indigo-100 bg-indigo-50">
                <div class="flex items-center text-sm text-indigo-600">
                  <i class="mr-2 fas fa-info-circle"></i>
                  <p>
                    Seret dan lepas baris untuk mengubah urutan widget. Gunakan
                    ikon <i class="mx-1 fas fa-grip-vertical"></i> untuk menarik
                    widget dan posisikan di antara widget lain. Perubahan akan
                    otomatis disimpan.
                  </p>
                </div>
              </div>
              <div class="overflow-x-auto">
                <table class="min-w-full divide-y divide-gray-300">
                  <thead class="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                      >
                        #
                      </th>
                      <th
                        scope="col"
                        class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                      >
                        Nama Widget
                      </th>
                      <th
                        scope="col"
                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 hidden md:table-cell"
                      >
                        Komponen
                      </th>
                      <th
                        scope="col"
                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 hidden lg:table-cell"
                      >
                        Deskripsi
                      </th>
                      <th
                        scope="col"
                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Urutan
                      </th>
                      <th
                        scope="col"
                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Status
                      </th>
                      <th scope="col" class="relative py-3.5 pl-3 pr-4 sm:pr-6">
                        <span class="sr-only">Aksi</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody
                    ref="widgetList"
                    class="bg-white divide-y divide-gray-200"
                  >
                    <tr
                      v-for="(widget, index) in filteredWidgets.data"
                      :key="widget.id"
                      :data-id="widget.id"
                      class="relative transition-colors duration-150 hover:bg-gray-50"
                    >
                      <td
                        class="flex items-center py-4 pl-4 pr-3 text-sm font-medium text-gray-900 whitespace-nowrap sm:pl-6"
                      >
                        <div
                          class="px-2 py-2 mr-1 -ml-2 rounded cursor-move hover:bg-gray-100 drag-handle"
                          title="Seret untuk mengubah posisi"
                        >
                          <i class="text-gray-400 fas fa-grip-vertical"></i>
                        </div>
                        {{ index + 1 }}
                      </td>
                      <td
                        class="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 whitespace-nowrap sm:pl-6"
                      >
                        {{ widget.name }}
                      </td>
                      <td
                        class="hidden px-3 py-4 text-sm text-gray-500 md:table-cell"
                      >
                        {{ widget.component }}
                      </td>
                      <td
                        class="hidden px-3 py-4 text-sm text-gray-500 lg:table-cell"
                      >
                        {{ widget.description || "-" }}
                      </td>
                      <td
                        class="px-3 py-4 text-sm text-gray-500 whitespace-nowrap"
                      >
                        {{ widget.order }}
                      </td>
                      <td
                        class="px-3 py-4 text-sm text-gray-500 whitespace-nowrap"
                      >
                        <span
                          :class="{
                            'bg-green-100 text-green-700': widget.is_active,
                            'bg-red-100 text-red-700': !widget.is_active,
                          }"
                          class="px-3 py-1 text-xs font-medium rounded-full"
                        >
                          {{ widget.is_active ? "Aktif" : "Nonaktif" }}
                        </span>
                      </td>
                      <td
                        class="relative py-4 pl-3 pr-4 text-sm font-medium text-right whitespace-nowrap sm:pr-6"
                      >
                        <div class="flex items-center justify-end space-x-2">
                          <button
                            type="button"
                            @click="editWidget(widget)"
                            class="text-indigo-600 hover:text-indigo-900"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            @click="toggleWidgetStatus(widget)"
                            :class="{
                              'text-red-600 hover:text-red-900':
                                widget.is_active,
                              'text-green-600 hover:text-green-900':
                                !widget.is_active,
                            }"
                          >
                            {{ widget.is_active ? "Nonaktifkan" : "Aktifkan" }}
                          </button>
                          <button
                            type="button"
                            @click="deleteWidget(widget)"
                            class="text-red-600 hover:text-red-900"
                          >
                            Hapus
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </Container>
</template>

<script setup>
import { Head, Link, useForm, router } from "@inertiajs/vue3";
import { nextTick, onMounted, ref, computed } from "vue";
import Sortable from "sortablejs";
import Container from "@/Layouts/Container.vue";
import Header from "@/Components/Header.vue";
import Breadcrumbs from "@/Components/Breadcrumbs.vue";
import Swal from "sweetalert2";

const props = defineProps({
  widgets: Array,
  availableComponents: Object,
});

const search = ref("");
const isEditMode = ref(false);
const widgetList = ref(null);

// Filter widgets based on search query
const filteredWidgets = computed(() => {
  if (!search.value) return props.widgets;

  return props.widgets.filter(
    (widget) =>
      widget.name.toLowerCase().includes(search.value.toLowerCase()) ||
      widget.component.toLowerCase().includes(search.value.toLowerCase()) ||
      (widget.description &&
        widget.description.toLowerCase().includes(search.value.toLowerCase()))
  );
});

// Form for creating/editing widgets
const form = useForm({
  id: null,
  name: "",
  component: "",
  description: "",
  order: 0,
  is_active: true,
  config: {},
});

// Inisialisasi sortable untuk drag & drop reordering
onMounted(() => {
  nextTick(() => {
    if (widgetList.value) {
      Sortable.create(widgetList.value, {
        handle: ".drag-handle", // Menggunakan ikon sebagai handle
        animation: 150,
        ghostClass: "bg-indigo-50", // Class yang diterapkan pada "ghost" element saat dragging
        chosenClass: "bg-indigo-100", // Class yang diterapkan pada item yang dipilih
        dragClass: "sortable-drag shadow-xl",
        // Indikator posisi saat drag
        forceFallback: true,
        fallbackClass: "sortable-fallback",
        // Konfigurasi tambahan untuk perbaikan penempatan
        fallbackOnBody: true,
        fallbackTolerance: 5,
        // Pointer events untuk menunjukkan elemen tersedia untuk drop
        emptyInsertThreshold: 15,
        // Delay sebelum memulai drag
        delay: 150,
        delayOnTouchOnly: true,

        onStart: (evt) => {
          // Tambahkan class ke item yang sedang di-drag untuk efek visual
          evt.item.classList.add("scale-105");
          // Tambahkan class ke container untuk menampilkan indikator drop
          widgetList.value.classList.add("dragging-active");
        },
        onChange: (evt) => {
          // Update indikator visual saat posisi berubah
          const rows = widgetList.value.querySelectorAll("tr");
          rows.forEach((row) => {
            row.classList.remove("drop-target-above", "drop-target-below");
          });

          if (evt.related) {
            const relatedIndex =
              evt.newIndex > evt.oldIndex ? evt.newIndex : evt.newIndex - 1;
            if (relatedIndex >= 0 && relatedIndex < rows.length) {
              rows[relatedIndex].classList.add("drop-target-below");
            }
          }
        },
        onEnd: (evt) => {
          // Hapus class dari item setelah selesai di-drag
          evt.item.classList.remove("scale-105");
          // Hapus class container
          widgetList.value.classList.remove("dragging-active");
          // Hapus indikator drop dari semua baris
          const rows = widgetList.value.querySelectorAll("tr");
          rows.forEach((row) => {
            row.classList.remove("drop-target-above", "drop-target-below");
          });

          // Kumpulkan ID yang telah diurutkan
          const orderedIds = Array.from(widgetList.value.children).map((item) =>
            parseInt(item.getAttribute("data-id"))
          );

          // Kirim ke server untuk menyimpan urutan baru
          reorderForm.ordered_ids = orderedIds;
          reorderForm.post(route("widgets.reorder"), {
            preserveScroll: true,
            onSuccess: () => {
              Swal.fire({
                title: "Berhasil!",
                text: "Urutan widget berhasil diperbarui.",
                icon: "success",
                confirmButtonText: "OK",
                timer: 1500,
                timerProgressBar: true,
              });
            },
            onError: () => {
              Swal.fire({
                title: "Gagal!",
                text: "Terjadi kesalahan saat memperbarui urutan widget.",
                icon: "error",
                confirmButtonText: "OK",
              });
            },
          });
        },
      });
    }
  });
});

const reorderForm = useForm({
  ordered_ids: [],
});

// Handle form submission for create/update
const submitForm = () => {
  if (isEditMode.value) {
    form.put(route("widgets.update", form.id), {
      onSuccess: () => {
        isEditMode.value = false;
        form.reset();
        form.id = null;

        Swal.fire({
          title: "Berhasil!",
          text: "Widget berhasil diperbarui.",
          icon: "success",
          confirmButtonText: "OK",
        });
      },
      onError: (errors) => {
        Swal.fire({
          title: "Gagal!",
          text: "Terjadi kesalahan saat memperbarui widget.",
          icon: "error",
          confirmButtonText: "OK",
        });
      },
    });
  } else {
    form.post(route("widgets.store"), {
      onSuccess: () => {
        form.reset();

        Swal.fire({
          title: "Berhasil!",
          text: "Widget berhasil dibuat.",
          icon: "success",
          confirmButtonText: "OK",
        });
      },
      onError: (errors) => {
        Swal.fire({
          title: "Gagal!",
          text: "Terjadi kesalahan saat membuat widget.",
          icon: "error",
          confirmButtonText: "OK",
        });
      },
    });
  }
};

// Edit widget
const editWidget = (widget) => {
  isEditMode.value = true;
  form.id = widget.id;
  form.name = widget.name;
  form.component = widget.component;
  form.description = widget.description || "";
  form.order = widget.order;
  form.is_active = widget.is_active;
  form.config = widget.config || {};

  // Scroll to form
  window.scrollTo({
    top: 0,
    behavior: "smooth",
  });
};

// Cancel editing
const cancelEdit = () => {
  isEditMode.value = false;
  form.reset();
  form.id = null;
};

// Delete widget
const deleteWidget = (widget) => {
  Swal.fire({
    title: "Apakah Anda yakin?",
    text: `Widget "${widget.name}" akan dihapus!`,
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#dc2626",
    cancelButtonColor: "#6b7280",
    confirmButtonText: "Ya, hapus!",
    cancelButtonText: "Batal",
  }).then((result) => {
    if (result.isConfirmed) {
      router.delete(route("widgets.destroy", widget.id), {
        onSuccess: () => {
          Swal.fire({
            title: "Berhasil!",
            text: "Widget berhasil dihapus.",
            icon: "success",
            confirmButtonText: "OK",
          });
        },
        onError: () => {
          Swal.fire({
            title: "Gagal!",
            text: "Terjadi kesalahan saat menghapus widget.",
            icon: "error",
            confirmButtonText: "OK",
          });
        },
      });
    }
  });
};

// Toggle widget active status
const toggleWidgetStatus = (widget) => {
  router.put(
    route("widgets.toggle-status", widget.id),
    {},
    {
      onSuccess: () => {
        Swal.fire({
          title: "Berhasil!",
          text: `Widget berhasil ${
            widget.is_active ? "dinonaktifkan" : "diaktifkan"
          }.`,
          icon: "success",
          confirmButtonText: "OK",
          timer: 1500,
          timerProgressBar: true,
        });
      },
      onError: () => {
        Swal.fire({
          title: "Gagal!",
          text: "Terjadi kesalahan saat mengubah status widget.",
          icon: "error",
          confirmButtonText: "OK",
        });
      },
    }
  );
};
</script>

<style>
.cursor-move {
  cursor: move;
}

.sortable-drag {
  background-color: #f3f4f6 !important;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
  opacity: 0.8;
  border: 1px dashed #6366f1;
}

.sortable-fallback {
  opacity: 0.8;
  transform: scale(1.05);
  background-color: #f3f4f6 !important;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.2),
    0 4px 6px -2px rgba(0, 0, 0, 0.1) !important;
  border: 2px solid #6366f1 !important;
  pointer-events: none;
}

.drag-handle {
  cursor: grab;
}

.drag-handle:active {
  cursor: grabbing;
}

.scale-105 {
  transform: scale(1.05);
  transition: transform 0.2s;
}

.dragging-active tr {
  position: relative;
}

.dragging-active tr::after {
  content: "";
  display: block;
  height: 3px;
  background-color: transparent;
  position: absolute;
  bottom: -2px;
  left: 0;
  right: 0;
  z-index: 5;
  transition: background-color 0.2s;
}

.dragging-active tr::before {
  content: "";
  display: block;
  height: 3px;
  background-color: transparent;
  position: absolute;
  top: -2px;
  left: 0;
  right: 0;
  z-index: 5;
  transition: background-color 0.2s;
}

.dragging-active tr:hover::after {
  background-color: rgba(99, 102, 241, 0.3);
}

.drop-target-above::before {
  background-color: #6366f1 !important;
}

.drop-target-below::after {
  background-color: #6366f1 !important;
}

/* Animasi untuk indikator drop */
@keyframes pulse-border {
  0% {
    border-color: rgba(99, 102, 241, 0.5);
  }
  50% {
    border-color: rgba(99, 102, 241, 1);
  }
  100% {
    border-color: rgba(99, 102, 241, 0.5);
  }
}

.dragging-active tr:hover {
  border: 1px dashed #6366f1;
  animation: pulse-border 1s infinite;
}
</style>
